import { PaperAirplaneIcon } from "@heroicons/react/20/solid";
import { useEffect, useRef, useState } from "react";
import { Message } from "../../hooks/use-chat";
import { Theme, useTheme } from "../../context/theme";

interface ChatInputProps {
  agents: string[];
  darkMode: boolean;
  loading: boolean;
  messages: Message[];
  input: string;
  setInput: (m: string) => any;
  onClick: () => any;
  appendMessage: (m?: string) => any;
}

export default function ChatInput({
  agents,
  darkMode,
  loading,
  input,
  setInput,
  onClick,
}: ChatInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [filteredOptions, setFilteredOptions] = useState<string[]>([]);
  const { selectTheme } = useTheme();

  useEffect(() => {
    if (darkMode === false) {
      selectTheme(Theme.LIGHT);
    } else {
      selectTheme(Theme.DARK);
    }
  }, [darkMode, selectTheme]);

  const handleUserSelect = (option: string) => {
    const input = inputRef.current?.value || "";

    const updatedValue = input.replace(/@(\w+)?/, (match, p1) => {
      if (p1) {
        return `@${option} `;
      } else {
        return `@${option} `; // Or whatever default value you want to add when there's no string after "@"
      }
    });
    setInput(updatedValue);
    setShowOptions(false);
  };

  return (
    <div className="h-min w-full">
      <div className="max-w-5xl mx-auto">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onClick();
          }}
          className="w-full flex flex-col space-y-2 md:space-x-2 md:space-y-0 md:flex-row items-center relative h-min px-4 pb-4 bg-gray-50 dark:bg-[#2c2c35] rounded-t-lg justify-center"
        >
          <div className="relative flex w-full flex-col flex-1">
            {showOptions && (
              <div
                className={
                  filteredOptions?.length
                    ? "flex-1 px-4 w-full py-3 mb-1 bg-gray-100 dark:bg-[#1D1E20] rounded-lg border border-gray-600 text-gray-600"
                    : ""
                }
              >
                {filteredOptions?.map((agents, index) => (
                  <div>
                    <div
                      key={index}
                      onClick={() => handleUserSelect(agents)}
                      className={`w-full py-2 px-4 hover:bg-gray-300 hover:rounded-xl dark:hover:bg-gray-700 cursor-pointer`}
                    >
                      {agents}
                    </div>
                    {index !== filteredOptions.length - 1 && (
                      <div className="border border-gray-600 border-b mx-2" />
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="relative flex w-full flex-1">
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => {
                  const value = e.target.value;
                  setInput(value);
                  // Check if '@' is typed

                  if (value.includes("@") && agents) {
                    // Filter users based on input text after '@'
                    const filterText = value.split("@")[1];
                    const filtered = agents?.filter((agent) =>
                      agent.toLowerCase().includes(filterText.toLowerCase())
                    );
                    setFilteredOptions(filtered);
                    setShowOptions(true);
                  } else {
                    setShowOptions(false);
                  }
                }}
                placeholder="Type a message..."
                className="flex-1 pl-4 w-full pr-12 py-3 dark:bg-[#27282B] rounded-lg border border-gray-600 dark:text-gray-400"
              />

              <button
                disabled={loading}
                className="absolute right-0 top-0 px-3 h-12 text-gray-900 dark:text-blue-500"
                type="submit"
              >
                {loading === false ? (
                  <PaperAirplaneIcon
                    style={{ width: 28, height: 28, color: "#FF5650" }}
                  />
                ) : (
                  <div role="status">
                    <svg
                      aria-hidden="true"
                      className="w-6 h-6 text-gray-200 animate-spin dark:text-gray-600 fill-gray-900 dark:fill-blue-600"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="#FF5650"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="#F39537"
                      />
                    </svg>
                    <span className="sr-only">Loading...</span>
                  </div>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
      <div className="flex flex-row justify-center pb-2">
        <p
          className="dark:text-gray-400 text-sm hover:text-blue-500 dark:hover:text-blue-500 cursor-pointer"
          onClick={() => window?.open("https://sphereone.xyz", "_blank")}
        >
          Powered by SphereOne
        </p>
      </div>
    </div>
  );
}
